import React, { useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';
import { useCaloricInputs } from './CalorieCalc';

const GOALS = [
  { value: 'maintain', label: 'Maintain weight', delta: 0 },
  { value: 'mildLoss', label: 'Mild weight loss (0.25 kg/week)', delta: -250 },
  { value: 'loss', label: 'Weight loss (0.5 kg/week)', delta: -500 },
  { value: 'extLoss', label: 'Extreme weight loss (1 kg/week)', delta: -1000 },
  { value: 'mildGain', label: 'Mild weight gain (0.25 kg/week)', delta: 250 },
  { value: 'gain', label: 'Weight gain (0.5 kg/week)', delta: 500 },
];
const ACT = [
  { value: 1.2, label: 'Sedentary' },
  { value: 1.375, label: 'Light: exercise 1-3 times/week' },
  { value: 1.465, label: 'Moderate: exercise 4-5 times/week' },
  { value: 1.55, label: 'Active: 3-4 intense sessions/week' },
  { value: 1.725, label: 'Very Active: 6-7 intense sessions/week' },
  { value: 1.9, label: 'Extra Active: physical job' },
];

export default function MacroCalc() {
  const s = useCaloricInputs();
  const [goal, setGoal] = React.useState('maintain');
  const heightInputs = s.tab === 0
    ? <div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={s.ft} inches={s.inches} onFt={s.setFt} onIn={s.setInches} testidPrefix="mc-h" /></div>
    : <UnitInput value={s.cm} onChange={s.setCm} unit="cm" testid="mc-h-cm" />;
  const weightInput = s.tab === 0
    ? <UnitInput value={s.lb} onChange={s.setLb} unit="pounds" testid="mc-w-lb" />
    : <UnitInput value={s.kg} onChange={s.setKg} unit="kg" testid="mc-w-kg" />;

  const goalDelta = GOALS.find(g => g.value === goal).delta;
  const daily = s.tdee + goalDelta;

  // Three macro splits — Moderate carb, Lower carb, Higher carb
  const splits = [
    { name: 'Moderate Carb', p: 30, c: 40, f: 30 },
    { name: 'Lower Carb', p: 40, c: 20, f: 40 },
    { name: 'Higher Carb', p: 30, c: 50, f: 20 },
  ];
  const rows = splits.map(sp => [
    sp.name,
    `${Math.round(daily * sp.p/100 / 4)} g`,
    `${Math.round(daily * sp.c/100 / 4)} g`,
    `${Math.round(daily * sp.f/100 / 9)} g`,
  ]);

  return (
    <CalcShell look="cnet" title="Macro Calculator" subtitle="Suggested macronutrient and calorie needs based on activity and goals.">
      <CalcNetPanel
        slug="mc"
        activeTab={s.tab}
        onTabChange={s.setTab}
        onClear={() => { s.clear(); setGoal('maintain'); }}
        settings={
          <>
            <Row label="BMR Formula">
              <select value={s.formula} onChange={e => s.setFormula(e.target.value)} data-testid="mc-formula" className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="mifflin">Mifflin-St Jeor</option>
                <option value="harris">Revised Harris-Benedict</option>
                <option value="katch">Katch-McArdle</option>
              </select>
            </Row>
            {s.formula === 'katch' && <Row label="Body Fat"><UnitInput value={s.bodyFat} onChange={s.setBodyFat} unit="%" testid="mc-bf" /></Row>}
          </>
        }
        right={
          <div>
            <div className="text-lg font-semibold text-slate-800">Total calories</div>
            <div className="text-2xl font-bold text-[#2f9c3f]">{Math.round(daily).toLocaleString()} Calories/day</div>
            <p className="text-sm text-slate-600">TDEE {Math.round(s.tdee).toLocaleString()} · Goal adjustment {goalDelta >= 0 ? '+' : ''}{goalDelta}</p>
            <div className="mt-4">
              <ResultTable
                testid="mc-macro-table"
                headers={['Suggested Distribution', 'Protein', 'Carbs', 'Fat']}
                rows={rows}
              />
            </div>
            <p className="mt-3 text-xs text-slate-600">Protein: 4 kcal/g · Carbs: 4 kcal/g · Fat: 9 kcal/g. Adjust ratios to preference and training.</p>
          </div>
        }
      >
        <Row label="Age"><UnitInput value={s.age} onChange={s.setAge} testid="mc-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages 18 – 80</span></Row>
        <Row label="Gender"><RadioGroup name="mc-g" value={s.gender} onChange={s.setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Height">{heightInputs}</Row>
        <Row label="Weight">{weightInput}</Row>
        <Row label="Activity"><select value={s.activity} onChange={e => s.setActivity(+e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="mc-activity">{ACT.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></Row>
        <Row label="Your Goal"><select value={goal} onChange={e => setGoal(e.target.value)} className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="mc-goal">{GOALS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}</select></Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Compute BMR & TDEE, then apply goal adjustment (${goalDelta} kcal/day).`,
        `Daily target ≈ <b>${Math.round(daily).toLocaleString()}</b> Calories/day.`,
        `Distribute by macro split: Protein ${splits[0].p}%, Carbs ${splits[0].c}%, Fat ${splits[0].f}% (moderate).`,
      ]} />
    </CalcShell>
  );
}
